export { reportWebVitals } from "next-axiom";
import { tracedConsole } from "@r8er/lib/logging";
import "@r8er/styles/globals.css";
import { useRouter } from "next/compat/router";
import type { AppType } from "next/dist/shared/lib/utils";
import type { ErrorInfo, PropsWithChildren, RefObject } from "react";
import {
  Component,
  createContext,
  createRef,
  useCallback,
  useEffect,
  useState,
} from "react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { ConsentProvider, useConsent } from "react-hook-consent";
import ErrorPage, { type ErrorProps } from "./_error";

const sendPageviewHit = (url) =>
  window.gtag?.("config", process.env.GOOGLE_ANALYTICS_ID, { page_path: url });

export const LoaderContext = createContext({
  isLoading: false,
  setIsLoading: (val: boolean) => {},
  appRef: null as RefObject<HTMLElement>,
});

type UserVisibleError = NonNullable<ErrorProps["error"]>;

class ClientErrorBoundary extends Component<
  PropsWithChildren<{ onError: (error: unknown, info: ErrorInfo) => void }>,
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    this.props.onError(error, info);
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

const MyApp = ({ children }: PropsWithChildren<{}>) => {
  const router = useRouter();
  const appRef = createRef<HTMLElement>();

  const [minimumTime, setMinimumTime] = useState(500);
  const [minimumTimeElapsed, setMinimumTimeElapsed] = useState(true);
  const [loading, setLoading] = useState(false);
  const [clientError, setClientError] = useState<UserVisibleError | null>(null);

  const showClientError = useCallback(
    (message: string, error: unknown, metadata?: Record<string, unknown>) => {
      const reference = tracedConsole.error(message, { error, ...metadata });
      setClientError({
        id: reference.errorId ?? reference.logId,
        message: error instanceof Error ? error.message : String(error),
      });
    },
    [],
  );

  const restartTimeout = useCallback(() => {
    setMinimumTimeElapsed(false);
    setTimeout(() => {
      setMinimumTimeElapsed(true);
    }, minimumTime);

    //simulate random load time between 0 and 5 seconds
    const randomLoadTime = 4000;
    setLoading(true);
    //simulate loading
    setTimeout(() => {
      setLoading(false);
    }, randomLoadTime);
  }, [setMinimumTimeElapsed, setLoading, minimumTime]);

  const { hasConsent } = useConsent();

  const trackAnalytics = hasConsent("analytics");
  useEffect(() => {
    const handleRouteChange = (url) =>
      trackAnalytics ? sendPageviewHit(url) : null;
    const handleRouteChangeStart = () => {
      setClientError(null);
      setLoading(true);
    };
    const handleRouteChangeComplete = () => setLoading(false);
    const handleRouteChangeError = (
      error: (Error & { cancelled?: boolean }) | undefined,
      url: string,
    ) => {
      setLoading(false);
      if (!error || error.cancelled) return;
      showClientError("Client route change failed", error, { url });
    };

    router.events.on("routeChangeComplete", handleRouteChange);
    router.events.on("routeChangeStart", handleRouteChangeStart);
    router.events.on("routeChangeComplete", handleRouteChangeComplete);
    router.events.on("routeChangeError", handleRouteChangeError);

    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
      router.events.off("routeChangeStart", handleRouteChangeStart);
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
      router.events.off("routeChangeError", handleRouteChangeError);
    };
  }, [router.events, showClientError, trackAnalytics]);

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
      scriptProps={{
        async: false,
        defer: false,
        appendTo: "head",
        nonce: undefined,
      }}
    >
      <LoaderContext.Provider
        value={{
          isLoading: loading,
          setIsLoading: (val: boolean) => setLoading(val),
          appRef,
        }}
      >
        {clientError ? (
          <ErrorPage error={clientError} />
        ) : (
          <ClientErrorBoundary
            key={router.asPath}
            onError={(error, info) =>
              showClientError("Client render failed", error, {
                componentStack: info.componentStack,
              })
            }
          >
            {children}
          </ClientErrorBoundary>
        )}
      </LoaderContext.Provider>
    </GoogleReCaptchaProvider>
  );
};

const ConsentedApp: AppType = ({ Component, pageProps }) => {
  const pageError = (pageProps as { error?: { id?: string } }).error;
  if (pageError)
    tracedConsole.error("SSR error rendered", {
      errorId: pageError.id,
      error: pageError,
    });
  // @ts-ignore
  if (pageProps.error) return <ErrorPage error={pageProps.error} />;

  return (
    <ConsentProvider
      options={{
        services: [
          {
            id: "necessary",
            name: "Necessary",
            cookies: [
              {
                pattern: "sessionId",
              },
              {
                pattern: "sessionAuthenticated",
              },
            ],
            mandatory: true,
          },
          {
            id: "functional",
            name: "Functional",
            mandatory: false,
          },
          {
            id: "performance",
            name: "Performance",
            mandatory: false,
          },
          {
            id: "analytics",
            name: "Analytics",
            cookies: [
              {
                pattern: /_ga.*/,
              },
              {
                pattern: "AEC",
              },
              { pattern: "__gsas" },
              { pattern: "__gpi" },
              { pattern: "__gpi_optout" },
              { pattern: "NID" },
              { pattern: "DSID" },
              { pattern: "test_cookie" },
              { pattern: "id" },
              { pattern: "__gads" },
              { pattern: "GED_PLAYLIST_ACTIVITY" },
              { pattern: "ACLK_DATA" },
              { pattern: "pm_sess" },
              { pattern: "pm_sess_NNN" },
              { pattern: "aboutads_sessNNN" },
              { pattern: "FPAU" },
              { pattern: "ANID" },
              { pattern: "AID" },
              { pattern: "IDE" },
              { pattern: "TAID" },
              { pattern: "FPGCLDC" },
              { pattern: "_gcl_dc" },
              { pattern: "_gcl_au" },
              { pattern: "FLC" },
              { pattern: "RUL" },
              { pattern: "FCCDCF" },
              { pattern: "FCNEC" },
              { pattern: "FPGCLAW" },
              { pattern: "FPGCLGB" },
              { pattern: "_gcl_gb" },
              { pattern: /_gac_gb_.*/ },
              { pattern: "_gcl_aw" },
              { pattern: "1P_JAR" },
              { pattern: "Conversion" },
              { pattern: "YSC" },
              { pattern: "VISITOR_INFO1_LIVE" },
              { pattern: "VISITOR_INFO1_LIVE__k" },
              { pattern: "VISITOR_INFO1_LIVE__default" },
              { pattern: "FPLC" },
              { pattern: "_ga" },
              { pattern: /_gac_.*/ },
              { pattern: "_gid" },
              { pattern: /_gat.*/ },
              { pattern: "__utma" },
              { pattern: "__utmb" },
              { pattern: "__utmc" },
              { pattern: "__utmt" },
              { pattern: "__utmz" },
              { pattern: "__utmv" },
              { pattern: "AMP_TOKEN" },
              { pattern: "FPID" },
              { pattern: "GA_OPT_OUT" },
              { pattern: "_ga_.*" },
              { pattern: /_dc_gtm_.*/ },
              { pattern: "_gaexp" },
              { pattern: "_gaexp_rc" },
              { pattern: "_opt_awcid" },
              { pattern: "_opt_awmid" },
              { pattern: "_opt_awgid" },
              { pattern: "_opt_awkid" },
              { pattern: "_opt_utmc" },
              { pattern: "_gcl_gf" },
              { pattern: "_gcl_ha" },
              { pattern: "PAIDCONTENT" },
              { pattern: "_opt_expid" },
            ],
            mandatory: false,
          },
          {
            id: "advertisement",
            name: "Advertisement",
            mandatory: false,
          },
          {
            id: "others",
            name: "Others",
            mandatory: false,
          },
        ],
        theme: "dark",
      }}
    >
      <MyApp>
        <Component {...pageProps} />
      </MyApp>
    </ConsentProvider>
  );
};

export default ConsentedApp;
